import { InterfacePanelState, InterfaceSidebarState, ViewModeType } from "../generated/interface"
import { Gesture } from "./BaseGesture"

export default function () {
  const { isEditingTextInput, gestureLayers } = useGestures()
  const { state: mainViewState } = useMainView()
  const { selectFile, selectFiles, isSelectionEmpty } = useSelection()
  const { state: panelState, hidePanelAndPersist, showPanelAndPersist, togglePanelAndPersist, isShowingPanel } = usePanel()
  const { focusOnInputTurn } = useAgent()
  const { state: sidebarState, hideSidebarAndPersist, showSidebarAndPersist } = useSidebar()
  const { previewScale, updatePreviewScale } = useViewMode()
  const { switchCollectionViewMode, state: collectionViewState, getAllFilesInCollection, createNewFolderInCollection, getSortedFilesInCollection, currentlyActiveFolder } = useCollectionView()
  const { openInstantMenu } = useInstantMenu()
  const { emitInterfaceEvent } = useEvents()

  class GlobalKeyboardShortcut extends Gesture {
    override layer = gestureLayers.keyboardShortcut

    override async onKeyDown(
      keyboard: KeyboardEvent,
    ) {
      // TODO: ensure we aren't in any text input elements

      if (keyboard.key === "1" && this.commandKeyModifier(keyboard)) {
        this.capture()
        keyboard.preventDefault()
        switchCollectionViewMode(ViewModeType.Icon)
      }
      else if (keyboard.key === "2" && this.commandKeyModifier(keyboard)) {
        this.capture()
        keyboard.preventDefault()
        switchCollectionViewMode(ViewModeType.Grid)
      }
      else if (keyboard.key === "3" && this.commandKeyModifier(keyboard)) {
        this.capture()
        keyboard.preventDefault()
        switchCollectionViewMode(ViewModeType.Feed)
      }
      else if (keyboard.key === "4" && this.commandKeyModifier(keyboard)) {
        this.capture()
        keyboard.preventDefault()
        switchCollectionViewMode(ViewModeType.Detail)
      }
      else if (keyboard.key === "5" && this.commandKeyModifier(keyboard)) {
        this.capture()
        keyboard.preventDefault()
        switchCollectionViewMode(ViewModeType.Tree)
      }
      else if (keyboard.key === "6" && this.commandKeyModifier(keyboard)) {
        this.capture()
        keyboard.preventDefault()
        switchCollectionViewMode(ViewModeType.Column)
      }
      else if (keyboard.key === "7" && this.commandKeyModifier(keyboard)) {
        this.capture()
        keyboard.preventDefault()
        switchCollectionViewMode(ViewModeType.Table)
      }
      else if (keyboard.key === "8" && this.commandKeyModifier(keyboard)) {
        this.capture()
        keyboard.preventDefault()
        switchCollectionViewMode(ViewModeType.Gallery)
      }
      else if (["K", "k"].includes(keyboard.key) && this.commandKeyModifier(keyboard) && this.shiftKeyModifier(keyboard)) {
        this.capture()
        keyboard.preventDefault()
        this.toggleAgentPanel()
      }
      else if (keyboard.key === "k" && this.commandKeyModifier(keyboard)) {
        this.capture()
        keyboard.preventDefault()
        openInstantMenu()
      }
      else if (keyboard.key === "a" && this.commandKeyModifier(keyboard)) {
        if (isEditingTextInput())
          return
        if (["folder"].includes(collectionViewState.value.state)) {
          this.capture()
          keyboard.preventDefault()
          this.debug("Selecting all folder view items")
          selectFiles(Object.values(getAllFilesInCollection()), "collection-view")
        }
      }
      else if (keyboard.key === "d" && this.commandKeyModifier(keyboard)) {
        if (isEditingTextInput())
          return
        if (mainViewState.value.state !== "collection")
          return this.warn("cmd + d has no effect outside of collection view")
        this.capture()
        keyboard.preventDefault()
        this.debug("Creating a new folder in current view")
        const folder = currentlyActiveFolder()
        if (!isDefined(folder))
          return this.error("No folder found in current view, cannot create a new folder")
        createNewFolderInCollection(folder)
      }
      // Hides both panels
      else if (keyboard.key === "\\" && this.commandKeyModifier(keyboard)) {
        if (isEditingTextInput())
          return
        this.capture()
        keyboard.preventDefault()
        this.debug("Toggling sidebar and panel")
        if (
          sidebarState.value.state === InterfaceSidebarState.Favorites
          || panelState.value.visible
        ) {
          hideSidebarAndPersist()
          hidePanelAndPersist()
        }
        else {
          showSidebarAndPersist()
          showPanelAndPersist()
        }
      }
      // Increments preview scale by 0.5. Note that '=' is the key for cmd+plus
      else if (["+", "=", "Add", "ZoomIn"].includes(keyboard.key) && this.commandKeyModifier(keyboard)) {
        if (isEditingTextInput())
          return
        if (mainViewState.value.state !== "collection")
          return this.warn("Not changing preview size as are not in right mode")
        this.capture()
        keyboard.preventDefault()
        this.debug("Increasing preview size")
        updatePreviewScale(previewScale.value + 0.05)
      }
      // Decrements preview scale by 0.5
      else if (["-", "Subtract", "ZoomOut"].includes(keyboard.key) && this.commandKeyModifier(keyboard)) {
        if (isEditingTextInput())
          return
        if (mainViewState.value.state !== "collection")
          return this.warn("Not changing preview size as are not in right mode")
        this.capture()
        keyboard.preventDefault()
        this.debug("Decreasing preview size")
        updatePreviewScale(previewScale.value - 0.05)
      }
      // Info -- show info panel
      else if (keyboard.key === "i" && this.commandKeyModifier(keyboard)) {
        if (isEditingTextInput())
          return
        this.capture()
        keyboard.preventDefault()
        this.debug("Toggling info panel")
        togglePanelAndPersist(InterfacePanelState.Details)
      }
      else if (keyboard.key === "h" && this.commandKeyModifier(keyboard)) {
        if (isEditingTextInput())
          return
        this.capture()
        keyboard.preventDefault()
        this.debug("Toggling activity panel")
        togglePanelAndPersist(InterfacePanelState.Activity)
      }
      else if (
        (keyboard.key === "y" && this.commandKeyModifier(keyboard))
        || (keyboard.key === "z" && this.commandKeyModifier(keyboard) && this.shiftKeyModifier(keyboard))
        || keyboard.key === "Redo"
      ) {
        if (isEditingTextInput())
          return
        this.capture()
        keyboard.preventDefault()
        this.debug("Redo key pressed, redoing last action")
        await useUndoHistory().redoLastAction()
      }
      else if (
        (keyboard.key === "z" && this.commandKeyModifier(keyboard))
        || keyboard.key === "Undo"
      ) {
        if (isEditingTextInput())
          return
        this.capture()
        keyboard.preventDefault()
        this.debug("Undo key pressed, undoing last action")
        await useUndoHistory().undoLastAction()
      }
      else if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(keyboard.key) && isSelectionEmpty()) {
        if (isEditingTextInput())
          return
        const file = getSortedFilesInCollection()[0]
        if (file)
          selectFile(file, "collection-view")
        this.capture()
        keyboard.preventDefault()
      }
      else if (keyboard.key === "Escape") {
        // escape key generically causes a soft blur
        console.debug("Escape pressed, soft blurring")
        emitInterfaceEvent("afterSoftBlur", {})
      }
    }

    toggleAgentPanel = async () => {
      if (isShowingPanel(InterfacePanelState.Agent))
        hidePanelAndPersist()
      else {
        this.debug("Opening agent panel from keyboard shortcut")
        showPanelAndPersist(InterfacePanelState.Agent)
        await nextTick()
        focusOnInputTurn()
      }
    }

    /**
     * TODO: address different OS'es, and test on Windows
     * @param keyboard event
     * @returns whether the OS-specific modifier key is pressed
     */
    commandKeyModifier(keyboard: KeyboardEvent) {
      return keyboard.metaKey || keyboard.ctrlKey
    }

    shiftKeyModifier(keyboard: KeyboardEvent) {
      return keyboard.shiftKey
    }
  }
  return new GlobalKeyboardShortcut()
}
